import * as Cesium from 'cesium';
import { polygon as turfPolygon, featureCollection } from '@turf/helpers';
import { union } from '@turf/union';
const closeRing = (coords) => {
    const ring = coords.map(c => [c.lng, c.lat]);
    if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
        ring.push([ring[0][0], ring[0][1]]);
    }
    return ring;
};
export const createMergedBufferEntities = (dataSource, streams) => {
    var _a;
    const bufferPolygons = [];
    let bufferColor = null;
    streams.forEach(highlight => {
        if (highlight.bufferPolygons && highlight.bufferPolygons.length > 0) {
            for (const poly of highlight.bufferPolygons) {
                if (poly.exterior && poly.exterior.length >= 3) {
                    bufferPolygons.push(poly);
                }
            }
            if (!bufferColor) {
                bufferColor = highlight.highlightColor;
            }
        }
    });
    if (bufferPolygons.length === 0 || !bufferColor) {
        return { entities: [], primaryEntity: null };
    }
    const color = bufferColor;
    const features = [];
    for (const poly of bufferPolygons) {
        try {
            const rings = [closeRing(poly.exterior)];
            for (const hole of (_a = poly.holes) !== null && _a !== void 0 ? _a : []) {
                if (hole && hole.length >= 3)
                    rings.push(closeRing(hole));
            }
            features.push(turfPolygon(rings));
        }
        catch (e) {
            console.warn('Invalid polygon:', e);
        }
    }
    if (features.length === 0) {
        return { entities: [], primaryEntity: null };
    }
    let mergedPolygon = null;
    try {
        mergedPolygon = features.length === 1
            ? features[0]
            : union(featureCollection(features));
    }
    catch (e) {
        console.warn('Failed to union polygons:', e);
        return { entities: [], primaryEntity: null };
    }
    if (!mergedPolygon) {
        return { entities: [], primaryEntity: null };
    }
    const createPositionsFromCoords = (coords) => {
        return coords.map(c => Cesium.Cartesian3.fromDegrees(c[0], c[1]));
    };
    const createHierarchyFromPolygon = (polyCoords) => {
        const outerRing = createPositionsFromCoords(polyCoords[0]);
        const holes = polyCoords.slice(1).map(hole => new Cesium.PolygonHierarchy(createPositionsFromCoords(hole)));
        return new Cesium.PolygonHierarchy(outerRing, holes);
    };
    const fillColor = color.withAlpha(0.3);
    const outlineColor = color.withAlpha(0.8);
    const entities = [];
    let primaryEntity = null;
    if (mergedPolygon.geometry.type === 'Polygon') {
        const hierarchy = createHierarchyFromPolygon(mergedPolygon.geometry.coordinates);
        const entity = dataSource.entities.add({
            id: 'merged-stream-buffer-0',
            polygon: {
                hierarchy: hierarchy,
                material: new Cesium.ColorMaterialProperty(fillColor),
                outline: true,
                outlineColor: outlineColor,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                arcType: Cesium.ArcType.GEODESIC
            }
        });
        entities.push(entity);
        primaryEntity = entity;
    }
    else if (mergedPolygon.geometry.type === 'MultiPolygon') {
        mergedPolygon.geometry.coordinates.forEach((polyCoords, index) => {
            const hierarchy = createHierarchyFromPolygon(polyCoords);
            const entity = dataSource.entities.add({
                id: `merged-stream-buffer-${index}`,
                polygon: {
                    hierarchy: hierarchy,
                    material: new Cesium.ColorMaterialProperty(fillColor),
                    outline: true,
                    outlineColor: outlineColor,
                    outlineWidth: 2,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                }
            });
            entities.push(entity);
            if (index === 0) {
                primaryEntity = entity;
            }
        });
    }
    return { entities, primaryEntity };
};
